/**
 * Counts how far linking a connection to the Token Vault got.
 *
 * Every way this can fail leaves the user on the token they logged in with, which
 * is how Superblocks behaves without the feature at all. That is the right thing
 * to do and it means a completely dead rollout looks exactly like a healthy one
 * from the outside, so the only way to know whether anyone is being linked is to
 * count it.
 *
 * Read it as a funnel: `requested` is how often the server asked, `linked` is how
 * often it worked, and the steps between say where the rest stopped.
 */

import type { Counter } from "@opentelemetry/api";
import { metrics } from "@opentelemetry/api";

const METER_NAME = "superblocks-ui";
const COUNTER_NAME = "ui_token_vault_seed_total";

export type SeedStep =
  /** The server asked the browser to link a connection. */
  | "requested"
  /** Not attempted at all: embedded, already tried in this tab, refused before. */
  | "skipped"
  /** Auth0 would not hand over a My Account token. */
  | "token_refused"
  /** Auth0 accepted the token but would not start the handshake. */
  | "connect_refused"
  /** Sent to the identity provider; whether they finish is the next two steps. */
  | "started"
  /** The identity provider or the user turned it down. */
  | "refused"
  /** Came back with a code but Auth0 would not finish. */
  | "complete_refused"
  /** Auth0 holds a refresh token for this user now. */
  | "linked";

/**
 * Deliberately not the connection name. It identifies a customer, and a large
 * tenant has enough of them to make this expensive to store; the logs carry it
 * where an engineer needs to know which one.
 */
type SeedAttributes = {
  /**
   * Why a step ended, from a fixed set: an OAuth error code, or one of the skip
   * reasons. Never free text - this is a metric label.
   */
  reason?: string;
};

let counter: Counter | undefined;

function getCounter(): Counter {
  if (!counter) {
    counter = metrics.getMeter(METER_NAME).createCounter(COUNTER_NAME, {
      description: "Token Vault connection linking, counted by step.",
      unit: "{attempt}",
    });
  }
  return counter;
}

export function recordSeedStep(
  step: SeedStep,
  attributes: SeedAttributes = {},
): void {
  try {
    // `vault.` and not `token_vault.`: the log pipeline redacts any field whose
    // name contains "token" (or auth, session, key, secret, cookie). The seeding
    // logs pair with these attributes, and under the longer prefix every one of
    // them arrived as `[REDACTED]` - the funnel counted steps nobody could read.
    getCounter().add(1, {
      "vault.seed.step": step,
      ...(attributes.reason ? { "vault.seed.reason": attributes.reason } : {}),
    });
  } catch {
    // Telemetry not initialised, which is every test and early boot. Counting is
    // never worth failing a login over.
  }
}
